// src/components/Sidebar.tsx
import { useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router";
import { ExternalLink, Loader2, LucideIcon } from "lucide-react";
import { useSidebarStore } from "../stores/sidebarStore";

export interface SidebarItem {
  key: string;
  icon: LucideIcon;
  label: string;
  path: string;
}

export interface ActionSidebarItem {
  key: string;
  icon: LucideIcon;
  label: string;
  onAction: () => void;
  isLoading?: boolean;
  isExternal?: boolean;
}

export interface SidebarSection {
  label?: string;
  items: SidebarItem[];
  actionItems?: ActionSidebarItem[];
}

interface SidebarProps {
  sections: SidebarSection[];
}

export default function Sidebar({ sections }: SidebarProps) {
  const { setIsSidebarPresentOnPage, setMobileMenuOpen } = useSidebarStore();
  const location = useLocation();

  useEffect(() => {
    setIsSidebarPresentOnPage(true);
    return () => {
      setIsSidebarPresentOnPage(false);
      setMobileMenuOpen(false);
    };
  }, [setIsSidebarPresentOnPage, setMobileMenuOpen]);

  const activeSection = useMemo(() => {
    const path = location.pathname.split("/");
    return path[path.length - 1];
  }, [location.pathname]);

  const renderActionItem = (item: ActionSidebarItem) => {
    const Icon = item.icon;
    return (
      <li key={item.key}>
        <button
          onClick={item.onAction}
          disabled={item.isLoading}
          className="group text-soluud-text-primary hover:bg-soluud-background hover:text-soluud-text-primary flex w-full cursor-pointer items-center rounded-lg border-l-[3px] border-transparent px-3 py-2.5 transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {item.isLoading ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
          ) : (
            <Icon className="text-soluud-text-secondary group-hover:text-soluud-text-primary h-5 w-5 shrink-0 transition-colors duration-150" />
          )}
          <span className="ms-3 flex-1 text-left text-sm">{item.label}</span>
          {item.isExternal && !item.isLoading && (
            <ExternalLink className="text-soluud-text-tertiary h-3.5 w-3.5 shrink-0" />
          )}
        </button>
      </li>
    );
  };

  const renderItem = (item: SidebarItem) => {
    const Icon = item.icon;
    const isActive =
      item.path === ""
        ? activeSection === ""
        : location.pathname.includes(`/${item.path}`);

    return (
      <li key={item.key}>
        <NavLink
          to={item.path}
          className={`group flex items-center rounded-lg px-3 py-2.5 transition-colors duration-150 ${
            isActive
              ? "bg-soluud-primary-light border-soluud-primary text-soluud-primary border-l-[3px] font-medium"
              : "text-soluud-text-primary hover:bg-soluud-background hover:text-soluud-text-primary border-l-[3px] border-transparent"
          }`}
        >
          <Icon
            className={`h-5 w-5 shrink-0 transition-colors duration-150 ${
              isActive
                ? "text-soluud-primary"
                : "text-soluud-text-secondary group-hover:text-soluud-text-primary"
            }`}
          />
          <span className="ms-3 text-sm">{item.label}</span>
        </NavLink>
      </li>
    );
  };

  return (
    <aside
      id="default-sidebar"
      className="hidden w-64 sm:sticky sm:top-16 sm:block sm:h-[calc(100vh-4rem)] md:h-auto"
      aria-label="Sidebar"
    >
      <div className="bg-soluud-background-card border-soluud-border-subtle h-full overflow-y-auto border-r px-3 py-4">
        {sections.map((section, sectionIndex) => (
          <div key={section.label ?? sectionIndex}>
            {sectionIndex > 0 && (
              <div className="border-soluud-border-subtle mx-2 my-4 border-t" />
            )}
            {section.label && (
              <h3 className="text-soluud-text-tertiary mb-2 px-3 text-xs font-semibold tracking-wider uppercase">
                {section.label}
              </h3>
            )}
            <ul className="space-y-1 font-medium">
              {section.items.map(renderItem)}
              {section.actionItems?.map(renderActionItem)}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  );
}
